/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import { CanvasProvider, CanvasContext } from './CanvasContext';
import Canvas from './Canvas';
import Toolbar from './Toolbar';
import ToolOptions from './ToolOptions';
import ImageActionButton from './ImageActionButton';
import HelpOverlay from 'components/HelpOverlay';

import useTooltipSequence from 'components/useTooltipSequence';
import useRegisterTooltipTarget from 'components/useRegisterTooltipTarget';
import MainEditorTooltipLayer from 'components/MainEditorTooltipLayer';
import { MAIN_EDITOR_TOOLTIPS } from 'constants/editorTooltips';
import {
  hasCompletedEditorTooltips,
  EDITOR_TOOLTIPS_CHANGED_EVENT,
} from 'utils/tooltips';
import UndoRedoButton from 'components/buttons/UndoRedoButton';

export default function Editor({ image, setImage, fileName }) {
  const cropToolRef = useRef(null);
  const startOverRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const undoRedoRef = useRef(null);

  const [showCropGuard, setShowCropGuard] = useState(false);
  const [pendingNextTool, setPendingNextTool] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showTipsAgainButton, setShowTipsAgainButton] = useState(false);

  const mainTooltipSequence = useTooltipSequence(MAIN_EDITOR_TOOLTIPS, {
    enabled: true,
    initialDelay: 600,
    stepDelay: 1500,
    visibilityThreshold: 0,
    visibilityDelay: 0,
  });

  useRegisterTooltipTarget(mainTooltipSequence, 'start-over', startOverRef);
  useRegisterTooltipTarget(
    mainTooltipSequence,
    'upload-image',
    uploadButtonRef,
  );
  useRegisterTooltipTarget(mainTooltipSequence, 'undo-redo', undoRedoRef);

  useEffect(() => {
    const updateTipsAgainButton = () => {
      setShowTipsAgainButton(hasCompletedEditorTooltips());
    };

    updateTipsAgainButton();

    window.addEventListener(
      EDITOR_TOOLTIPS_CHANGED_EVENT,
      updateTipsAgainButton,
    );

    return () => {
      window.removeEventListener(
        EDITOR_TOOLTIPS_CHANGED_EVENT,
        updateTipsAgainButton,
      );
    };
  }, []);

  // note: image prop passed in is the image url, not the file obj
  return (
    <CanvasProvider>
      <CanvasContext.Consumer>
        {({
          activeTool,
          setActiveTool,
          imageRef,
          canvasRef,
          textItems,
          hasAddedTextRef,
          rotateGuardOpen,
          closeRotateGuard,
          confirmRotateGuardDiscard,
          undo,
          redo,
          undoStack,
          redoStack,
        }) => {
          const handleToolChange = (nextTool) => {
            const hasPendingCrop =
              activeTool === 'crop' &&
              cropToolRef.current?.hasCropSelection?.();

            if (hasPendingCrop && nextTool !== 'crop') {
              setPendingNextTool(nextTool);
              setShowCropGuard(true);
              return;
            }

            setActiveTool(nextTool);
          };

          return (
            <>
              <HelpOverlay
                isOpen={showHelp}
                onClose={() => setShowHelp(false)}
              />
              {rotateGuardOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                  <div
                    className="absolute inset-0 bg-black/40"
                    onClick={closeRotateGuard}
                  />

                  <div className="relative w-full max-w-md rounded-2xl bg-white p-5 shadow-[0_10px_40px_rgba(0,0,0,0.25)]">
                    <div className="text-base font-extrabold text-stone-800">
                      Rotation not applied
                    </div>
                    <div className="mt-2 text-sm text-stone-600 leading-relaxed">
                      You haven&apos;t clicked{' '}
                      <span className="font-semibold">Apply</span> yet. Click
                      Apply to keep the rotation, or Cancel rotation to leave
                      without it.
                    </div>

                    <div className="mt-5 flex items-center justify-end gap-2">
                      <button
                        className="px-3 py-2 rounded-xl bg-white border border-stone-200 text-stone-800 hover:bg-stone-50"
                        onClick={closeRotateGuard}
                      >
                        Stay
                      </button>
                      <button
                        className="px-3 py-2 rounded-xl bg-stone-800 text-white hover:bg-stone-900"
                        onClick={confirmRotateGuardDiscard}
                      >
                        Cancel rotation
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {showCropGuard && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg w-[90%] max-w-[340px] text-center">
                    <p className="mb-4">
                      You have a crop selection. Apply it before leaving the
                      crop tool?
                    </p>

                    <div className="flex justify-center gap-2 flex-nowrap">
                      <button
                        className="flex-1 px-3 py-2 text-sm whitespace-nowrap bg-gray-300 rounded"
                        onClick={() => {
                          setShowCropGuard(false);
                          setPendingNextTool(null);
                        }}
                      >
                        Cancel
                      </button>

                      <button
                        className="flex-1 px-3 py-2 text-sm whitespace-nowrap bg-gray-400 rounded"
                        onClick={() => {
                          cropToolRef.current?.clearCropOverlay?.();
                          setActiveTool(pendingNextTool);
                          setPendingNextTool(null);
                          setShowCropGuard(false);
                        }}
                      >
                        Exit
                      </button>

                      <button
                        className="flex-1 px-3 py-2 text-sm whitespace-nowrap bg-button text-white rounded"
                        onClick={() => {
                          cropToolRef.current?.applyCrop?.();
                          setActiveTool(pendingNextTool);
                          setPendingNextTool(null);
                          setShowCropGuard(false);
                        }}
                      >
                        Apply Crop
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="w-full text-sm my-2 px-2 lg:px-0 lg:pr-2">
                <div className="flex flex-wrap items-center justify-center md:justify-end gap-2">
                  <button
                    ref={startOverRef}
                    className="border border-md-beige px-3 py-1.5 rounded-xl hover:bg-md-beige transition duration-300"
                    onClick={() => {
                      setImage('');
                    }}
                  >
                    Start over
                  </button>

                  <div
                    ref={undoRedoRef}
                    className="inline-flex items-center gap-1"
                  >
                    <UndoRedoButton
                      onClick={undo}
                      disabled={undoStack.length === 0}
                      icon={`${process.env.PUBLIC_URL}/images/undo.png`}
                      disabledIcon={`${process.env.PUBLIC_URL}/images/undo-disabled.png`}
                      title="Undo"
                    />

                    <UndoRedoButton
                      onClick={redo}
                      disabled={redoStack.length === 0}
                      icon={`${process.env.PUBLIC_URL}/images/redo.png`}
                      disabledIcon={`${process.env.PUBLIC_URL}/images/redo-disabled.png`}
                      title="Redo"
                    />
                  </div>

                  <ImageActionButton
                    buttonRef={uploadButtonRef}
                    imageRef={imageRef}
                    canvasRef={canvasRef}
                    textItems={textItems}
                    hasAddedTextRef={hasAddedTextRef}
                    fileName={fileName}
                  />

                  {showTipsAgainButton ? (
                    <button
                      type="button"
                      onClick={mainTooltipSequence.showTooltipsAgain}
                      className="
          inline-flex items-center gap-1.5 rounded-full border border-md-beige
          bg-white px-3 py-1.5 text-xs font-medium text-brown
          transition hover:bg-cream hover:border-brown/50
          focus:outline-none focus:ring-2 focus:ring-brown/30
        "
                      title="Show editor tips again"
                    >
                      <span aria-hidden="true">💡</span>
                      <span>Tips</span>
                    </button>
                  ) : null}

                  <button
                    title="Help"
                    aria-label="Open help"
                    className="bg-button hover:bg-lt-brown transition duration-300 text-white rounded-full h-[26px] w-[26px] font-bold"
                    onClick={() => setShowHelp(true)}
                  >
                    ?
                  </button>
                </div>
              </div>

              <div className="editor relative flex flex-col md:flex-row">
                <div className="w-full md:w-1/5 bg-lt-beige p-4 overflow-x-auto snap-x snap-always scroll-smooth">
                  <Toolbar
                    activeTool={activeTool}
                    setActiveTool={handleToolChange}
                    mainTooltipSequence={mainTooltipSequence}
                  />
                </div>

                <Canvas image={image} />
              </div>

              {activeTool !== null ? (
                <ToolOptions
                  activeTool={activeTool}
                  cropToolRef={cropToolRef}
                />
              ) : (
                <div className="pl-2 lg:pl-0 py-4 font-bold">
                  Select a tool to edit the image
                </div>
              )}

              <MainEditorTooltipLayer sequence={mainTooltipSequence} />
            </>
          );
        }}
      </CanvasContext.Consumer>
    </CanvasProvider>
  );
}
